import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Input, notification, Spin, Pagination, InputNumber } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, Save, Package } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { fetchMaterialLog, updateMaterialLog, updateDsrLogSections } from '../../redux/dsr/action';
import './TechnicianLogSection.css';
import './MaterialConsumptionSection.css';

const { TextArea } = Input;

const MaterialConsumptionSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSubmitted,
  onComplete,
  onCancel,
  onFetchMaterialLog,
  onUpdateMaterialLog,
  isMaterialLogLoading,
  isUpdatingMaterialLog,
  currentMaterialLog,
  onUpdateDsrLogSections,
  isUpdatingDsrLogSections,
  isSkipped,
  isShiftSkipped = false,
  onSkipChange,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });
  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  const fetchLogData = useCallback(() => {
    if (selectedProject && selectedDate && currentDsrLog) {
      const user = JSON.parse(localStorage.getItem('user'));
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftValue = shift === 'day' ? '0' : '1';

      onFetchMaterialLog({
        projectId: selectedProject.id,
        logDate: logDate,
        userEmail: user.email,
        shift: shiftValue,
        sowId: selectedMainline?.id,
      });
    }
  }, [selectedProject, selectedDate, currentDsrLog, shift, selectedMainline, onFetchMaterialLog]);

  useEffect(() => {
    fetchLogData();
  }, [fetchLogData]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  useEffect(() => {
    return () => {
      setRowData([]);
      setComments('');
      setSkipped(false);
      setIsDirty(false);
    };
  }, []);

  useEffect(() => {
    if (currentMaterialLog && Array.isArray(currentMaterialLog)) {
      const formattedData = currentMaterialLog.map((item) => ({
        ...item,
        totalRemaining: (item.pendingQuantityInStock || 0) - (parseFloat(item.totalUsedToday) || 0),
      }));
      setRowData(formattedData);

      if (currentMaterialLog.length > 0) {
        const shiftValue = shift === 'day' ? '0' : '1';
        setComments(
          shiftValue === '0'
            ? (currentMaterialLog[0].dlmDayShiftComments || '')
            : (currentMaterialLog[0].dlmNightShiftComments || '')
        );
      }
    }
  }, [currentMaterialLog, shift]);

  const totalRows = rowData.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rowData.slice(start, start + pageSize);
  }, [rowData, page, pageSize]);

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  /* Same rule as legacy grid: only rows with strictly positive "stock (start)" get an editable used field; zero or negative stock → read-only. */
  const canEditUsed = (row) => {
    if (skipped || isSubmitted) return false;
    const n = Number(row?.pendingQuantityInStock ?? 0);
    return !Number.isNaN(n) && n > 0;
  };

  const handleTotalUsedNumberChange = (row, val) => {
    if (!canEditUsed(row)) return;
    const pendingStock = Number(row.pendingQuantityInStock ?? 0);
    const usedValue = val == null ? 0 : val;
    if (usedValue > pendingStock) {
      setRowData((prev) => {
        const idx = prev.findIndex((r) => r.rowKey === row.rowKey);
        if (idx < 0) return prev;
        const data = { ...prev[idx] };
        notification.warning({
          message: 'Invalid Quantity',
          description: `Used quantity (${usedValue}) cannot exceed pending stock (${pendingStock}). Value will be reset to 0.`,
          placement: 'bottomRight',
        });
        data.totalUsedToday = 0;
        data.totalRemaining = (data.pendingQuantityInStock || 0) - 0;
        const next = [...prev];
        next[idx] = data;
        return next;
      });
      setIsDirty(true);
      return;
    }
    setRowData((prev) => {
      const idx = prev.findIndex((r) => r.rowKey === row.rowKey);
      if (idx < 0) return prev;
      const data = { ...prev[idx] };
      data.totalUsedToday = usedValue;
      data.totalRemaining = (data.pendingQuantityInStock || 0) - (parseFloat(usedValue) || 0);
      const next = [...prev];
      next[idx] = data;
      return next;
    });
    setIsDirty(true);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

    if (onSkipChange) {
      onSkipChange(e);
    } else {
      if (currentDsrLog?.id) {
        const submissionStatus = generateSubmissionStatus('material', shift, true);
        const sectionsSkipped = {
          ...currentDsrLog.sectionsSkipped,
          isMaterialUsageSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedDayShift || 0),
          isMaterialUsageSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedNightShift || 0),
        };

        onUpdateDsrLogSections({
          dsrLogId: currentDsrLog.id,
          submissionStatus: submissionStatus,
          sectionsSkipped: sectionsSkipped,
        });
      }
    }
  };

  const generateSubmissionStatus = (section, sh, submittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: submittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'material');
      return;
    }

    const dsrLogId = rowData.length > 0 ? rowData[0].dsrLogId : currentDsrLog?.id;

    if (!dsrLogId) {
      notification.error({ message: 'Cannot determine DSR Log ID to save data.' });
      return;
    }

    try {
      const shiftValue = shift === 'day' ? '0' : '1';
      const dsrLogMaterial = rowData.map((item) => ({
        projectMaterialId: item.projectMaterialId,
        dsrLogId: dsrLogId,
        totalUsedToday: item.totalUsedToday || '0',
        shift: shiftValue,
      }));

      const payload = {
        dsrLogMaterial,
        comments: {
          dsrLogId: dsrLogId,
          dlmDayShiftComments: shiftValue === '0' ? comments : (currentMaterialLog[0]?.dlmDayShiftComments || ''),
          dlmNightShiftComments: shiftValue === '1' ? comments : (currentMaterialLog[0]?.dlmNightShiftComments || ''),
        },
      };

      await onUpdateMaterialLog(payload);
      setIsDirty(false);

      const submissionStatus = generateSubmissionStatus('material', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isMaterialUsageSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedDayShift || 0),
        isMaterialUsageSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isMaterialUsageSkippedNightShift || 0),
      };

      await onUpdateDsrLogSections({
        dsrLogId: dsrLogId,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      if (onComplete) onComplete(true, 'material');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = shift === 'day' ? '0' : '1';

        onFetchMaterialLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving material consumption section:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save material consumption data. Please try again.',
      });
    }
  };

  return (
    <div className="modern-dsr-tech-log-section modern-dsr-mc-usage">
      <Spin
        spinning={isMaterialLogLoading}
        indicator={(
          <span className="modern-dsr-tech-log-spin" aria-hidden>
            <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
          </span>
        )}
        tip="Loading material consumption data..."
        size="large"
      >
        {isShiftSkipped ? (
          <div className="modern-dsr-tech-log-skipped-banner">
            <strong>
              {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
            </strong>
            <p>
              This section is disabled because the {shift} shift has been skipped.
            </p>
          </div>
        ) : (
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Package size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Material consumption</h3>
                  <p className="modern-dsr-tech-log-card-sub">Record used quantities per line item for this shift.</p>
                </div>
              </div>
            </div>

            <div className="modern-dsr-tech-table" role="table" aria-label="Material consumption">
              <div
                className="modern-dsr-tech-thead modern-dsr-mc-grid"
                role="row"
              >
                <div className="modern-dsr-tech-th modern-dsr-mc-th--tight" role="columnheader">Material category</div>
                <div className="modern-dsr-tech-th modern-dsr-mc-th--tight" role="columnheader">Name</div>
                <div className="modern-dsr-tech-th modern-dsr-mc-th--tight" role="columnheader">Batch #</div>
                <div className="modern-dsr-tech-th modern-dsr-mc-th--tight" role="columnheader">Expiry</div>
                <div className="modern-dsr-tech-th modern-dsr-mc-th--tight" role="columnheader">Product code</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-mc-th--tight" role="columnheader">Unit</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-mc-th--tight" role="columnheader">Stock (start)</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-mc-th--tight" role="columnheader">Used (shift)</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center modern-dsr-mc-th--tight" role="columnheader">Remaining</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">No material lines loaded.</div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.rowKey}
                    className="modern-dsr-tech-row modern-dsr-mc-grid"
                    role="row"
                    onMouseEnter={() => setHoveredRowKey(row.rowKey)}
                    onMouseLeave={() => setHoveredRowKey(null)}
                    data-active={hoveredRowKey === row.rowKey ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly" role="cell" title={row.materialCategory}>
                      {row.materialCategory}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly" role="cell" title={row.materialName}>
                      {row.materialName}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly" role="cell" title={row.batchNumber}>
                      {row.batchNumber}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly" role="cell" title={row.expiryDate}>
                      {row.expiryDate ? moment(row.expiryDate).format('YYYY-MM-DD') : ''}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly" role="cell" title={row.productCode}>
                      {row.productCode}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--readonly modern-dsr-mc-td--muted" role="cell">
                      {row.unit}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--num modern-dsr-mc-td--qty modern-dsr-mc-td--qty-center" role="cell">
                      {row.pendingQuantityInStock}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--num modern-dsr-mc-td--qty modern-dsr-mc-td--qty-center" role="cell">
                      {canEditUsed(row) ? (
                        <div className="modern-dsr-tech-hours-wrap">
                          <InputNumber
                            className="modern-dsr-tech-inputnum"
                            value={row.totalUsedToday}
                            min={0}
                            max={Number(row.pendingQuantityInStock ?? 0) || 0}
                            onChange={(v) => handleTotalUsedNumberChange(row, v == null ? 0 : v)}
                            disabled={skipped || isSubmitted}
                          />
                        </div>
                      ) : (
                        <span className="modern-dsr-mc-td--num modern-dsr-mc-td--qty-center-text">{row.totalUsedToday}</span>
                      )}
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-mc-td--num modern-dsr-mc-td--qty modern-dsr-mc-td--qty-center" role="cell">
                      {row.totalRemaining}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>

            <div className="modern-dsr-tech-log-issues-block">
              <div className="modern-dsr-tech-log-issues-label">Comments</div>
              <TextArea
                className="modern-dsr-tech-log-issues-textarea"
                rows={3}
                placeholder="Add any comments here..."
                value={comments}
                onChange={(e) => { setComments(e.target.value); setIsDirty(true); }}
                disabled={skipped || isSubmitted}
              />
            </div>
          </div>
        )}
      </Spin>

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={(isUpdatingMaterialLog || isUpdatingDsrLogSections) ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingMaterialLog || isUpdatingDsrLogSections || isSubmitted || isShiftSkipped}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isMaterialLogLoading: get(dsrReducer, 'isMaterialLogLoading', false),
  isUpdatingMaterialLog: get(dsrReducer, 'isUpdatingMaterialLog', false),
  currentMaterialLog: get(dsrReducer, 'currentMaterialLog', null),
  isUpdatingDsrLogSections: get(dsrReducer, 'isUpdatingDsrLogSections', false),
});

const mapDispatchToProps = {
  onFetchMaterialLog: fetchMaterialLog,
  onUpdateMaterialLog: updateMaterialLog,
  onUpdateDsrLogSections: updateDsrLogSections,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialConsumptionSection);
